import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { getGigsafeRoleSaving, getGigsafeRoles } from 'src/selectors/gigsafeUser'
import { isDefaultGigsafeRole } from './GigsafeRoleForm.validator'
import type { GigsafeRoleFormValues } from './GigsafeRoleForm.interface'

interface GigsafeRoleGate {
  /** A built-in role is selected: its name and permissions are fixed, so saving is pointless. */
  isDefaultRole: boolean
  /** No role selected — the form is creating one rather than editing an existing one. */
  isCreating: boolean
  isSaving: boolean
}

/**
 * Shared by the fields (which disable themselves and explain why) and the modal footer's submit
 * (which is portalled out of the `<Form>` but stays inside the Formik provider). Both have to
 * agree on the built-in-role lock, so the rule lives here rather than being derived twice.
 */
export const useGigsafeRoleGate = (): GigsafeRoleGate => {
  const { values } = useFormikContext<GigsafeRoleFormValues>()
  const roles = useSelector(getGigsafeRoles)
  const isSaving = useSelector(getGigsafeRoleSaving)

  // Derived from role IDENTITY (the selected `roleId`), not from the live, freely-typed `name` —
  // in create mode `roleId` is `''` and no role was ever selected, so an admin typing toward a
  // built-in name (e.g. `manager`) must stay able to keep typing. The reserved-name rejection for
  // a brand-new role is the validator's job (`not-a-default-role`), not this gate.
  const selectedRole = roles.find((role) => {
    return role.id === values.roleId
  })

  return {
    isDefaultRole: Boolean(selectedRole && isDefaultGigsafeRole(selectedRole.name)),
    isCreating: values.roleId === '',
    isSaving,
  }
}
